import { useTranslation } from '../../../i18n';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Table, Button, message, Popconfirm, Tag, Space, Empty, Modal, Form, Input, Select, Progress, Card, Steps, Alert, Row, Col, Tooltip, Checkbox, Divider } from 'antd';
import { DeleteOutlined, AppstoreOutlined, DownloadOutlined, CloudDownloadOutlined, ReloadOutlined, CheckCircleOutlined, GlobalOutlined, RocketOutlined } from '@ant-design/icons';
import hostSharedService from '../../../../shared/services/hostSharedService';

const CATEGORY_ICONS = { cms: '🌐', blog: '✍️', forum: '💬', shop: '🛒', tool: '🛠️', other: '📦' };
const WEBAPP_STORE_URL = '/lightapp/store';

const WebappManager = ({ vhostName, vhostId, isAdmin = false }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [dataSource, setDataSource] = useState([]);
    const [availableApps, setAvailableApps] = useState([]);

    // Install wizard state
    const [wizardVisible, setWizardVisible] = useState(false);
    const [wizardStep, setWizardStep] = useState(0); // 0=选应用, 1=配置, 2=下载安装, 3=完成
    const [selectedApp, setSelectedApp] = useState(null);
    const [domainInfo, setDomainInfo] = useState(null);
    const [domainLoading, setDomainLoading] = useState(false);
    const [configForm] = Form.useForm();

    // Progress state
    const [progressPhase, setProgressPhase] = useState(''); // downloading, installing
    const [progressPercent, setProgressPercent] = useState(0);
    const [progressStatus, setProgressStatus] = useState('active');
    const [progressText, setProgressText] = useState('');
    const [installResult, setInstallResult] = useState(null);

    // Custom app mode
    const [customMode, setCustomMode] = useState(false);
    const [customForm] = Form.useForm();

    const pollTimerRef = useRef(null);

    useEffect(() => {
        if (vhostName) loadData();
        loadAvailableApps();
        return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    }, [vhostName]);

    const loadAvailableApps = async () => {
        try {
            const res = await hostSharedService.getPublicWebappList();
            if (res.success && Array.isArray(res.data)) {
                setAvailableApps(res.data);
            }
        } catch {
            // fallback: empty list, user can still use custom input
        }
    };

    const svc = useCallback(() => {
        if (isAdmin && vhostId) {
            return {
                getWebapp: () => hostSharedService.adminGetWebapp(vhostId),
                uninstallWebapp: (id) => hostSharedService.adminUninstallWebapp(vhostId, id),
                getWebappDomainInfo: () => hostSharedService.adminGetWebappDomainInfo(vhostId),
                downloadWebapp: (appid) => hostSharedService.adminDownloadWebapp(vhostId, appid),
                getWebappDownloadStatus: (appid) => hostSharedService.adminGetWebappDownloadStatus(vhostId, appid),
                installWebapp: (params) => hostSharedService.adminInstallWebapp(vhostId, params),
                getWebappInstallStatus: (phyDir, op) => hostSharedService.adminGetWebappInstallStatus(vhostId, phyDir, op),
                completeWebappInstall: (id) => hostSharedService.adminCompleteWebappInstall(vhostId, id),
            };
        }
        return {
            getWebapp: () => hostSharedService.getWebapp(vhostName),
            uninstallWebapp: (id) => hostSharedService.uninstallWebapp(vhostName, id),
            getWebappDomainInfo: () => hostSharedService.getWebappDomainInfo(vhostName),
            downloadWebapp: (appid) => hostSharedService.downloadWebapp(vhostName, appid),
            getWebappDownloadStatus: (appid) => hostSharedService.getWebappDownloadStatus(vhostName, appid),
            installWebapp: (params) => hostSharedService.installWebapp(vhostName, params),
            getWebappInstallStatus: (phyDir, op) => hostSharedService.getWebappInstallStatus(vhostName, phyDir, op),
            completeWebappInstall: (id) => hostSharedService.completeWebappInstall(vhostName, id),
        };
    }, [vhostName, vhostId, isAdmin]);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await svc().getWebapp();
            if (response.success) {
                const items = Array.isArray(response.data) ? response.data : [];
                setDataSource(items.map((item, index) => ({ ...item, key: item.id ?? index })));
            } else {
                message.error(response.error || t('Failed to load web apps'));
                setDataSource([]);
            }
        } catch (error) {
            message.error(t('Failed to load: {{message}}', { message: error.message }));
            setDataSource([]);
        } finally {
            setLoading(false);
        }
    };

    const handleUninstall = async (id) => {
        setLoading(true);
        try {
            const response = await svc().uninstallWebapp(id);
            if (response.success) {
                message.success(t('Uninstalled successfully'));
                loadData();
            } else {
                message.error(response.error || t('Uninstall failed'));
            }
        } catch (error) {
            message.error(t('Uninstall failed: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    // ========================
    // Install wizard
    // ========================

    const openWizard = () => {
        setWizardStep(0);
        setSelectedApp(null);
        setDomainInfo(null);
        setCustomMode(false);
        setInstallResult(null);
        setProgressPhase('');
        setProgressPercent(0);
        configForm.resetFields();
        customForm.resetFields();
        setWizardVisible(true);
    };

    const selectApp = async (app) => {
        setSelectedApp(app);
        setWizardStep(1);
        configForm.setFieldsValue({ dir: app.dir || '' });

        // Load domain info
        setDomainLoading(true);
        try {
            const response = await svc().getWebappDomainInfo();
            if (response.success) {
                // Flatten nested arrays from EP API (each domain may be ['domain'] instead of 'domain')
                const rawDomains = response.data?.domains || [];
                const domains = rawDomains.map(d => Array.isArray(d) ? d[0] : d).filter(Boolean);
                const info = { ...response.data, domains };
                setDomainInfo(info);
                if (domains.length > 0) {
                    configForm.setFieldsValue({ domain: domains[0] });
                }
            }
        } catch (error) {
            message.warning(t('Failed to retrieve domains. Please enter one manually'));
        } finally {
            setDomainLoading(false);
        }
    };

    const selectCustomApp = async () => {
        try {
            const values = await customForm.validateFields();
            selectApp({
                appid: values.appid,
                name: values.appname || values.appid,
                version: values.appver || '最新版',
                dir: values.dir || '',
            });
        } catch {
            // validation failed
        }
    };

    const startInstall = async () => {
        try {
            const values = await configForm.validateFields();
            setWizardStep(2);

            // Phase 1: Download
            setProgressPhase('downloading');
            setProgressText(t('Downloading app installation package...'));
            setProgressPercent(0);
            setProgressStatus('active');

            const dlResp = await svc().downloadWebapp(selectedApp.appid);
            if (!dlResp.success) {
                throw new Error(dlResp.error || t('Download failed'));
            }

            // Poll download status
            await pollStatus('download', selectedApp.appid);

            // Phase 2: Install
            setProgressPhase('installing');
            setProgressText(t('Installing app...'));
            setProgressPercent(0);
            setProgressStatus('active');

            const domain = Array.isArray(values.domain) ? values.domain[0] : values.domain;
            const installResp = await svc().installWebapp({
                appid: selectedApp.appid,
                appname: selectedApp.name || '',
                appver: selectedApp.version || '',
                domain: domain,
                dir: values.dir || '',
                force: values.force || false,
            });

            if (!installResp.success) {
                throw new Error(installResp.error || t('Installation failed'));
            }

            const installData = installResp.data || {};

            // Poll install status
            if (installData.phy_dir) {
                await pollStatus('install', null, installData.phy_dir);
            }

            // Mark install as complete in EP database (status 0→1)
            if (installData.id) {
                try {
                    await svc().completeWebappInstall(installData.id);
                } catch (e) {
                    console.warn('标记安装完成失败:', e);
                }
            }

            // Done!
            setWizardStep(3);
            setInstallResult({
                id: installData.id || null,
                installUrl: installData.install_url || null,
                domain: values.domain,
                dir: values.dir || '/',
            });
            message.success(t('App installed!'));
            loadData();

        } catch (error) {
            if (pollTimerRef.current) clearInterval(pollTimerRef.current);
            if (error.errorFields) return;
            setProgressStatus('exception');
            setProgressText(t('Operation failed: {{message}}', { message: error.message }));
            message.error(error.message);
        }
    };

    const pollStatus = (type, appid, phyDir) => {
        return new Promise((resolve, reject) => {
            let attempts = 0;
            pollTimerRef.current = setInterval(async () => {
                attempts++;
                try {
                    let resp;
                    if (type === 'download') {
                        resp = await svc().getWebappDownloadStatus(appid);
                    } else {
                        resp = await svc().getWebappInstallStatus(phyDir, 'install');
                    }

                    if (!resp.success) {
                        if (attempts > 60) { clearInterval(pollTimerRef.current); reject(new Error(t('Operation timed out'))); }
                        return;
                    }

                    const data = resp.data || {};
                    const code = data.code;

                    if (code === 200) {
                        setProgressPercent(100);
                        setProgressStatus('success');
                        clearInterval(pollTimerRef.current);
                        resolve();
                    } else if (type === 'download' && code === 201 && data.total > 0) {
                        setProgressPercent(Math.round((data.finished / data.total) * 100));
                    } else if (code === 500) {
                        clearInterval(pollTimerRef.current);
                        reject(new Error(type === 'download' ? t('Download failed') : t('Installation failed')));
                    } else if (attempts > 120) {
                        clearInterval(pollTimerRef.current);
                        reject(new Error(t('Operation timed out')));
                    }
                } catch (e) {
                    if (attempts > 120) { clearInterval(pollTimerRef.current); reject(e); }
                }
            }, 2000);
        });
    };

    // ========================
    // Render
    // ========================

    const columns = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
        {
            title: t('App name'),
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Space><AppstoreOutlined /><strong>{text}</strong></Space>,
        },
        { title: t('Version'), dataIndex: 'version', key: 'version', width: 100, render: (t) => t ? <Tag color="blue">{t}</Tag> : '-' },
        { title: t('Domains'), dataIndex: 'domain', key: 'domain', width: 180 },
        { title: t('Installation directory'), dataIndex: 'dir', key: 'dir', width: 120 },
        {
            title: t('Actions'), key: 'action', width: 100,
            render: (_, record) => (
                <Popconfirm
                    title={t('Uninstall this app?')}
                    description={t('Uninstalling will delete the app\'s data!')}
                    onConfirm={() => handleUninstall(record.id)}
                    okText={t('Confirm uninstall')} cancelText={t('Cancel')} okButtonProps={{ danger: true }}
                >
                    <Button type="link" danger size="small" icon={<DeleteOutlined />}>{t('Uninstall')}</Button>
                </Popconfirm>
            ),
        },
    ];

    const renderStepSelect = () => (
        <div>
            <div style={{ marginBottom: 16, color: '#666' }}>{t('Select the web app to install:')}</div>
            <Row gutter={[12, 12]}>
                {availableApps.map(app => (
                    <Col span={12} key={app.appid}>
                        <Card
                            hoverable
                            size="small"
                            onClick={() => selectApp({
                                appid: app.appid,
                                name: app.name,
                                version: app.version || '',
                                dir: '',
                            })}
                            style={{ cursor: 'pointer' }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                {app.icon && app.icon.startsWith('http') ? (
                                    <img src={app.icon} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                                ) : (
                                    <span style={{ fontSize: 28 }}>{CATEGORY_ICONS[app.category] || '📦'}</span>
                                )}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600 }}>{app.name} <span style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>{app.version}</span></div>
                                    <div style={{ fontSize: 12, color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{app.description}</div>
                                </div>
                            </div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Divider style={{ margin: '16px 0' }}>{t('Or')}</Divider>

            {!customMode ? (
                <div style={{ textAlign: 'center' }}>
                    <Button type="dashed" onClick={() => setCustomMode(true)}>
                        {t('Install another app (enter app ID)')}
                    </Button>
                    <span style={{ margin: '0 8px', color: '#999' }}>|</span>
                    <Button type="link" icon={<GlobalOutlined />} onClick={() => window.open(WEBAPP_STORE_URL, '_blank')}>
                        {t('Browse app store')}
                    </Button>
                </div>
            ) : (
                <Card size="small" title={t('Custom app')}>
                    <Form form={customForm} layout="inline" style={{ flexWrap: 'wrap', gap: 8 }}>
                        <Form.Item name="appid" rules={[{ required: true, message: t('Required') }]} style={{ marginBottom: 8 }}>
                            <Input placeholder={t('App ID (e.g. wordpress)')} style={{ width: 180 }} />
                        </Form.Item>
                        <Form.Item name="appname" style={{ marginBottom: 8 }}>
                            <Input placeholder={t('App name (optional)')} style={{ width: 150 }} />
                        </Form.Item>
                        <Form.Item name="appver" style={{ marginBottom: 8 }}>
                            <Input placeholder={t('Version (optional)')} style={{ width: 120 }} />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 8 }}>
                            <Button type="primary" onClick={selectCustomApp}>{t('Next step')}</Button>
                        </Form.Item>
                    </Form>
                </Card>
            )}
        </div>
    );

    const renderStepConfig = () => (
        <div>
            <Card size="small" style={{ marginBottom: 16, background: '#f6f8fa' }}>
                <Space>
                    <span style={{ fontSize: 24 }}>{selectedApp?.icon || '📦'}</span>
                    <div>
                        <strong>{selectedApp?.name}</strong>
                        {selectedApp?.version && <Tag color="blue" style={{ marginLeft: 8 }}>{t(selectedApp.version)}</Tag>}
                    </div>
                </Space>
            </Card>

            <Form form={configForm} layout="vertical">
                <Form.Item label={t('Installation domain')} name="domain" rules={[{ required: true, message: t('Select or enter the installation domain') }]}>
                    <Select placeholder={t('Select an installation domain')} loading={domainLoading}>
                        {(domainInfo?.domains || []).map(d => (
                            <Select.Option key={d} value={d}>{d}</Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label={t('Installation directory')}
                    name="dir"
                    extra={t('Leave blank to install in the website\'s root directory. To install in a subdirectory, enter its name, e.g., blog.')}
                >
                    <Input placeholder={t('Leave blank for the root directory, or enter a subdirectory name')} />
                </Form.Item>

                <Form.Item name="force" valuePropName="checked">
                    <Checkbox>{t('Skip domain DNS check (force installation if the domain does not yet resolve to the server)')}</Checkbox>
                </Form.Item>

                <Alert
                    type="info"
                    showIcon
                    message={t('Installation instructions')}
                    description={
                        <ul style={{ paddingLeft: 20, margin: '4px 0' }}>
                            <li>{t('After installation, you will be redirected to the app\'s initial setup page')}</li>
                            <li>{t('On the initial setup page, you can set the admin username, password, and other details')}</li>
                            <li>{t('View database details under "Databases" in the shared hosting control panel')}</li>
                        </ul>
                    }
                    style={{ marginBottom: 16 }}
                />
            </Form>
        </div>
    );

    const renderStepProgress = () => (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <div style={{ marginBottom: 24 }}>
                <Progress
                    type="circle"
                    percent={progressPercent}
                    status={progressStatus}
                    size={120}
                />
            </div>
            <div style={{ marginBottom: 8 }}>
                {progressPhase === 'downloading' && (
                    <div>
                        <CloudDownloadOutlined style={{ fontSize: 20, marginRight: 8, color: '#1890ff' }} />
                        <strong>{t("Downloading the {{name}} installation package", { name: selectedApp?.name })}</strong>
                    </div>
                )}
                {progressPhase === 'installing' && (
                    <div>
                        <RocketOutlined style={{ fontSize: 20, marginRight: 8, color: '#52c41a' }} />
                        <strong>{t("Installing {{name}}", { name: selectedApp?.name })}</strong>
                    </div>
                )}
            </div>
            <div style={{ color: '#999' }}>{progressText}</div>
            {progressStatus === 'exception' && (
                <div style={{ marginTop: 16 }}>
                    <Button onClick={() => { setWizardStep(1); setProgressStatus('active'); }}>
                        {t('Go back and retry')}
                    </Button>
                </div>
            )}
        </div>
    );

    const renderStepComplete = () => (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
            <h3>{t("{{name}} installed successfully!", { name: selectedApp?.name })}</h3>

            {installResult?.installUrl ? (
                <Alert
                    type="success"
                    style={{ textAlign: 'left', marginTop: 16 }}
                    message={t('Please complete the initial setup')}
                    description={
                        <div>
                            <p>{t('The app has been installed on your shared hosting. Visit the following address to complete initial setup:')}</p>
                            <p style={{ margin: '8px 0' }}>
                                <strong>{t('Setup URL:')}</strong>
                                <a href={installResult.installUrl} target="_blank" rel="noopener noreferrer">
                                    {installResult.installUrl}
                                </a>
                            </p>
                            <p style={{ color: '#666', fontSize: 13 }}>
                                {t('On the initial setup page, you need to:')}<br/>
                                {t('1. Set the admin username and password')}<br/>
                                {t('2. Configure the database connection (see "Database management")')}<br/>
                                {t('3. Complete other basic settings')}
                            </p>
                        </div>
                    }
                />
            ) : (
                <Alert
                    type="success"
                    style={{ textAlign: 'left', marginTop: 16 }}
                    message={t('Installation complete')}
                    description={
                        <p>
                            {t('App installed to ')}<strong>{installResult?.domain}</strong>{t("{{value}}. You can visit the domain directly to see the result.", { value: installResult?.dir ? `/${installResult.dir}` : '' })}
                        </p>
                    }
                />
            )}
        </div>
    );

    const getWizardFooter = () => {
        if (wizardStep === 0) return null; // step 0 uses inline buttons
        if (wizardStep === 1) return [
            <Button key="back" onClick={() => setWizardStep(0)}>{t('Previous step')}</Button>,
            <Button key="install" type="primary" icon={<RocketOutlined />} onClick={startInstall}>
                {t('Start installation')}
            </Button>
        ];
        if (wizardStep === 2) return null; // progress, no buttons
        if (wizardStep === 3) return [
            installResult?.installUrl && (
                <Button key="setup" type="primary" icon={<GlobalOutlined />}
                    onClick={() => window.open(installResult.installUrl, '_blank')}>
                    {t('Go to initial setup')}
                </Button>
            ),
            <Button key="close" onClick={() => setWizardVisible(false)}>{t('Close')}</Button>
        ].filter(Boolean);
        return null;
    };

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                <Button icon={<DownloadOutlined />} type="primary" onClick={openWizard}>
                    {t('Install app')}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={loadData}>
                    {t('Refresh')}
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={{ pageSize: 10 }}
                size="small"
                locale={{ emptyText: <Empty description={t('No web apps installed yet')} /> }}
            />

            <Modal
                title={
                    <Space>
                        <AppstoreOutlined />
                        <span>{t('Install web app')}</span>
                        {selectedApp && wizardStep > 0 && (
                            <Tag>{selectedApp.name}</Tag>
                        )}
                    </Space>
                }
                open={wizardVisible}
                onCancel={() => {
                    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                    setWizardVisible(false);
                }}
                footer={getWizardFooter()}
                width={680}
                closable={wizardStep !== 2 || progressStatus === 'exception'}
                maskClosable={false}
            >
                <Steps
                    current={wizardStep}
                    size="small"
                    style={{ marginBottom: 24 }}
                    items={[
                        { title: t('Select an app') },
                        { title: t('Installation settings') },
                        { title: t('Download and install') },
                        { title: t('Done') },
                    ]}
                />

                {wizardStep === 0 && renderStepSelect()}
                {wizardStep === 1 && renderStepConfig()}
                {wizardStep === 2 && renderStepProgress()}
                {wizardStep === 3 && renderStepComplete()}
            </Modal>
        </div>
    );
};

export default WebappManager;
